import { yupResolver } from '@hookform/resolvers/yup';
import React, { FC, useMemo } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import Radio from 'src/components/Radio/Radio';
import { CURRENCY, MAX_LENGTH } from 'src/constants/common';
import { RootState } from 'src/redux/store';
import * as yup from 'yup';

import { ILoadInsurance, ILoadInsuranceForm } from './quoteDetailModal.interface';

const LoadInsuranceModal: FC<ILoadInsurance> = ({ handleClose, handleLoadInsurance, defaultFormValues }) => {
    const { t } = useTranslation();
    const { currency } = useSelector((state: RootState) => state.basic);

    const schema = yup.object().shape({
        insuranceCommercialValue: yup
            .string()
            .required('Commercial value is required')
            .test('isValidValue', 'Commercial value cannot be 0', (value) => {
                if (!value) return false;

                const num = parseFloat(value);

                return !isNaN(num) && num > 0;
            }),

        insuranceCurrencyType: yup.string().required('Currency type is required'),
        insuranceCommodity: yup.string().required('Commodity is required'),
    });

    const onSubmit = (data: ILoadInsuranceForm) => {
        handleLoadInsurance(data);
    };

    const defaultValues = useMemo(
        () => ({
            insuranceCommercialValue: defaultFormValues?.insuranceCommercialValue || '',
            insuranceCurrencyType: defaultFormValues?.insuranceCurrencyType || currency || CURRENCY.CAD,
            insuranceCommodity: defaultFormValues?.insuranceCommodity || '',
        }),
        [defaultFormValues]
    );

    const methods = useForm<ILoadInsuranceForm>({
        resolver: yupResolver(schema) as unknown as Resolver<ILoadInsuranceForm>,
        defaultValues,
    });

    const {
        handleSubmit,
        control,
        formState: { isDirty },
    } = methods;

    return (
        <>
            <CommonModal
                title={t('Common.Load_Insurance')}
                handleClose={handleClose}
                size={'max-w-[450px]'}
                secondaryBtnText={t('Common.Cancel')}
                primaryBtnText={t('Common.Save_Changes')}
                primaryBtnOnClick={handleSubmit(onSubmit)}
                secondaryBtnOnClick={handleClose}
                primaryBtnDisabled={!isDirty}
            >
                <div className="p-5">
                    <div className="rounded-xl border border-utilityGray200 bg-gray50 p-4 flex gap-2.5 flex-col">
                        <Controller
                            name="insuranceCommercialValue"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    label={t('Common.Commercial_Value')}
                                    labelClassName="block mb-1.5"
                                    inputType="number"
                                    isAllowDecimal={true}
                                    maxLength={6}
                                    inputName="loadInsuranceValue"
                                    required={true}
                                    placeholder={t('QuotingHub.Common.Enter_commercial_value')}
                                    className={`form_control ${error?.message ? 'border border-red-500 border-solid pr-8' : ''}`}
                                    labelIconRight={
                                        <Controller
                                            name="insuranceCurrencyType"
                                            control={control}
                                            render={({ field: { value: loadInsuranceValueUnit, onChange: onChangeLoadCurrencyType } }) => (
                                                <div className="flex gap-2">
                                                    {Object.values(CURRENCY).map((currencyVal) => (
                                                        <div key={currencyVal} className="flex items-center gap-1">
                                                            <Radio
                                                                id={`load_insurance_currency_${currencyVal.toLowerCase()}`}
                                                                onChangeFunc={onChangeLoadCurrencyType}
                                                                inputName={'load_insurance_currency'}
                                                                value={currencyVal}
                                                                checked={loadInsuranceValueUnit === currencyVal}
                                                            />
                                                            <label
                                                                htmlFor={`load_insurance_currency_${currencyVal.toLowerCase()}`}
                                                                className="text-textSecondary text-xs font-medium uppercase cursor-pointer"
                                                            >
                                                                {currencyVal}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        />
                                    }
                                    value={value}
                                    shouldFormatNumber={true}
                                    onChangeFunc={onChange}
                                />
                            )}
                        />

                        <Controller
                            name="insuranceCommodity"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    label={t('Common.Commodity')}
                                    labelClassName="block mb-1.5"
                                    inputType="text"
                                    inputName="commodity"
                                    required={true}
                                    placeholder={t('QuotingHub.Common.Enter_commodity')}
                                    className={`form_control ${error?.message ? 'border border-red-500 border-solid pr-8' : ''}`}
                                    maxLength={MAX_LENGTH.COMMODITY}
                                    value={value}
                                    onChangeFunc={onChange}
                                />
                            )}
                        />
                    </div>
                </div>
            </CommonModal>
        </>
    );
};

export default LoadInsuranceModal;
